import { useEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { useMediaRemote, type MediaPlayerInstance } from '@vidstack/react';

import { ImageWithFallback } from '@virginexperiencedays/components-v2/src/layout/Image';
import { transformToLargeDir } from '@virginexperiencedays/components-v2/src/pages/product/HeroImages/transformToLargeDir';
import {
  MediaGrid as MediaGridContainer,
  MediaGridItem,
  type MediaCount,
} from '@virginexperiencedays/components-v2/src/MediaGrid';
import { VideoPlayerBunny } from '@virginexperiencedays/components-v2/src/VideoPlayer';
import type { Image, Video } from '@virginexperiencedays/products';
import { cn } from '@virginexperiencedays/tailwind';
import { screens } from '@virginexperiencedays/tailwind/screens';

import type { ExtendedMediaType } from './VariationPage/extractors';
import { DataTestId } from './VariationPage/PageUI';

type Props = {
  handleOpen?: (index: number) => void;
  isLightBoxOpen?: boolean;
  mediaCount: MediaCount;
  media: ExtendedMediaType[];
} & HTMLAttributes<HTMLDivElement>;

export const MediaGrid = ({ className, isLightBoxOpen, mediaCount, media, handleOpen }: Props) => {
  if (!media?.length) return null;

  return (
    <div
      className={cn(
        '-mx-4 flex h-[277px] flex-row flex-nowrap overflow-hidden overflow-x-auto px-4 hide-scrollbar sm:h-[340px]',
        className
      )}
      data-c="container"
    >
      <MediaGridContainer
        data-testid={DataTestId.MediaGrid}
        mediaCount={mediaCount}
        data-c="MediaGridContainer"
      >
        {media.map((mediaItem, i) => {
          return (
            <MediaGridItem
              key={mediaItem.type === 'video' ? mediaItem.videoId : mediaItem.url}
              itemPosition={i}
              mediaCount={mediaCount}
              className={cn(
                handleExplicitItemSize(mediaCount, i),
                media.length > 1 && 'max-w-[calc(100vw_-_64px)]'
              )}
              data-c="MediaGridItem"
            >
              <MediaGridItemContent i={i} handleOpen={handleOpen}>
                {mediaItem.type === 'image' ? (
                  <ImageWithFallback
                    src={handleAutoImageDir(mediaItem.url, media.length, i)}
                    fallbackSrc={mediaItem.url}
                    alt={(mediaItem as Image)?.altText ?? ''}
                    fill
                    priority
                    className="h-full w-full object-cover"
                    sizes={handleSizes(mediaCount, i)}
                    data-c="MediaGridItemImage"
                  />
                ) : (
                  <MediaGridItemVideo isLightBoxOpen={isLightBoxOpen} video={mediaItem} />
                )}
              </MediaGridItemContent>
            </MediaGridItem>
          );
        })}
      </MediaGridContainer>
    </div>
  );
};

type MediaGridItemContentProps = {
  children: ReactNode;
  i: number;
} & Pick<Props, 'handleOpen'>;

const MediaGridItemContent = ({ children, handleOpen, i }: MediaGridItemContentProps) => {
  return handleOpen ? (
    <button
      className="relative h-full w-full"
      data-c="MediaGridItemButton"
      onClick={() => handleOpen(i)}
    >
      {children}
    </button>
  ) : (
    <>{children}</>
  );
};

type MediaGridItemVideoProps = {
  isLightBoxOpen?: boolean;
  video: Video;
};

const MediaGridItemVideo = ({ isLightBoxOpen, video }: MediaGridItemVideoProps) => {
  const refPlayer = useRef<MediaPlayerInstance>(null);
  const refWasPaused = useRef(true);

  const remote = useMediaRemote(refPlayer);

  useEffect(() => {
    if (!remote) return;

    if (isLightBoxOpen) {
      refWasPaused.current = refPlayer.current?.paused;

      remote.pause();
    } else {
      !refWasPaused.current && remote.play();
    }
  }, [isLightBoxOpen, remote]);

  return (
    <div className="relative h-full w-full [&_[data-media-player]]:rounded-lg">
      <VideoPlayerBunny
        ref={refPlayer}
        className="absolute left-0 top-0 size-full"
        url={process.env.NEXT_PUBLIC_BUNNY_STREAM_CDN_BASE_URL}
        video={video}
        load="eager"
        posterLoad="eager"
        autoPlay
        loop
        muted
      />
    </div>
  );
};

/**
 * Determines the appropriate sizes attribute for an image based on the number of media items
 * and the position of the current item.
 *
 * @param mediaCount - The total number of media items.
 * @param itemPosition - The position of the current media item (0-based index).
 * @returns A string representing the sizes attribute for the image.
 */
const handleSizes = (mediaCount: MediaCount, itemPosition: number): string => {
  const { lg, md, sm } = screens;

  switch (mediaCount) {
    case 2:
      return `(min-width: ${md}px) 500px, (min-width: ${sm}px) 380px, 300px`;
    case 3:
      return itemPosition === 0
        ? `(min-width: ${md}px) 750px, (min-width: ${sm}px) 580px, 420px`
        : `(min-width: ${sm}px) 200px, 140px`;
    case 4:
      switch (itemPosition) {
        case 1:
        case 2:
          return `(min-width: ${sm}px) 200px, 140px`;
        default:
          return `(min-width: ${md}px) 500px, (min-width: ${sm}px) 340px, 300px`;
      }
    default:
      return `(min-width: ${lg}px) 900px, 100vw`;
  }
};

/**
 * Function to handle automatic IMGIX image directory transformation based on media count and item position.
 *
 * @param {string} src - The source URL of the image.
 * @param {number} mediaCount - The total number of media items.
 * @param {number} itemPosition - The position of the current media item.
 * @returns {string} - The transformed image URL if applicable, otherwise the original URL.
 */
function handleAutoImageDir(src: string, mediaCount: number, itemPosition: number): string {
  switch (mediaCount) {
    case 2:
      return transformToLargeDir(src);

    case 3:
      return itemPosition === 0 ? transformToLargeDir(src) : src;

    case 4:
      switch (itemPosition) {
        case 0:
        case 3:
          return transformToLargeDir(src);
        default:
          return src;
      }

    default:
      return transformToLargeDir(src);
  }
}

/**
 * Determines the explicit size of a media item based on the total number of media items
 * and the position of the current item.
 *
 * The requirement for explicit sizing is due to the behaviour of iOS Safari when using object-fit: cover.
 *
 * @param mediaCount - The total number of media items.
 * @param itemPosition - The position of the current media item (0-based index).
 * @returns A string representing the CSS classes for the width and height of the media item.
 */
function handleExplicitItemSize(mediaCount: MediaCount, itemPosition: number): string {
  switch (mediaCount) {
    case 2:
      return 'w-[277px] h-[277px] sm:w-full sm:h-[340px]';

    case 3:
      return itemPosition === 0
        ? 'w-[277px] h-[277px] sm:w-full sm:h-[340px]'
        : 'w-[135px] h-[135px] sm:w-full sm:h-[166px]';

    case 4:
      switch (itemPosition) {
        case 0:
        case 3:
          return 'w-[277px] h-[277px] sm:w-full sm:h-[340px]';
        default:
          return 'w-[135px] h-[135px] sm:w-[166px] sm:h-[166px]';
      }

    default:
      return 'h-[277px] sm:h-[340px] w-full';
  }
}
